import { useCallback, useMemo, useRef, useState } from "react";
import type { Bounds, LatLng } from "@oshimap/domain";
import { toMarkerViewModel, type MapMarkerData, type MarkerViewModel } from "@oshimap/map-core";
import { ep } from "../../lib/api";
import { queryKeys } from "@oshimap/query";
import { useQuery } from "../../lib/use-query";

export type MapQuery =
  | { mode: "bounds"; bounds: Bounds; category?: string }
  | { mode: "nearby"; center: LatLng; radiusKm: number; category?: string };

/** FR-MAP-15: bounds/radius 없이 전체를 훑지 않는다. 서버도 두 모드를 동시에 받으면 400 을 준다. */
export function useMapData(query: MapQuery, visitedKeys: ReadonlySet<string>) {
  /* 키는 Web·Native 가 같은 규칙으로 만든다 — 좌표는 반올림해서 미세한 흔들림으로
     캐시가 갈라지지 않게 한다 (queryKeys.mapBounds / mapNearby). */
  const key = useMemo(
    () => (query.mode === "bounds"
      ? queryKeys.mapBounds(query.bounds, query.category ? { category: query.category } : {})
      : queryKeys.mapNearby(query.center, query.radiusKm)),
    [query],
  );

  const { state, reload } = useQuery<MapMarkerData[]>(
    key,
    async (signal) => {
      if (query.mode === "bounds") {
        return ep.mapItemsByBounds(query.bounds, query.category ? { category: query.category } : {}, { signal });
      }
      return ep.mapItemsNearby(query.center, query.radiusKm, {}, { signal });
    },
  );

  const markers: MarkerViewModel[] = useMemo(() => {
    if (state.status !== "success") return [];
    return state.data.map((m) => toMarkerViewModel(m, { visitedKeys }));
  }, [state, visitedKeys]);

  return { state, markers, reload };
}

/** FR-MAP-02: 지도 중심 · 사용자 위치 · 검색 위치를 따로 관리한다. */
export function useMapLocations(initialCenter: LatLng) {
  const [camera, setCamera] = useState({ center: initialCenter, zoom: 13 });
  const [userLocation, setUserLocation] = useState<LatLng | null>(null);
  const [searchLocation, setSearchLocation] = useState<LatLng | null>(initialCenter);
  const [permission, setPermission] = useState<"unknown" | "granted" | "denied" | "unsupported">("unknown");
  const asking = useRef(false);

  const locate = useCallback(async (): Promise<LatLng | null> => {
    if (asking.current) return null;
    if (typeof navigator === "undefined" || !navigator.geolocation) { setPermission("unsupported"); return null; }
    asking.current = true;
    try {
      const pos = await new Promise<GeolocationPosition>((res, rej) =>
        navigator.geolocation.getCurrentPosition(res, rej, { timeout: 8000, maximumAge: 60_000 }));
      const p = { latitude: pos.coords.latitude, longitude: pos.coords.longitude };
      setPermission("granted"); setUserLocation(p); setCamera((c) => ({ ...c, center: p }));
      return p;
    } catch {
      setPermission("denied");                       // FR-ENT-04: 막다른 길이 아니다
      return null;
    } finally { asking.current = false; }
  }, []);

  return { camera, setCamera, userLocation, searchLocation, setSearchLocation, permission, setPermission, locate };
}
